import { useState, useEffect } from "react";
import axios from "axios";
import { Trash, ArrowCounterClockwise, Warning } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

export function TrashDialog({ open, onOpenChange, sessionId, API, onSuccess }) {
  const [trashItems, setTrashItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchTrash = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/data/trash?session_id=${sessionId}`);
      setTrashItems(response.data.items || []);
    } catch (err) {
      console.error("Error fetching trash:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchTrash();
  }, [open]);

  const handleRestore = async (rowId, name) => {
    try {
      await axios.post(`${API}/data/restore/${rowId}?session_id=${sessionId}`);
      fetchTrash();
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("Restore error:", err);
    }
  };

  const handlePermanentDelete = async (rowId, name) => {
    if (!window.confirm(`"${name}" endgültig löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/data/permanent-delete/${rowId}?session_id=${sessionId}`);
      fetchTrash();
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("Permanent delete error:", err);
    }
  };

  const handleEmptyTrash = async () => {
    if (!window.confirm("Papierkorb komplett leeren? Alle Einträge werden endgültig gelöscht!")) return;
    if (!window.confirm("Wirklich fortfahren? Diese Aktion kann NICHT rückgängig gemacht werden.")) return;

    try {
      for (const item of trashItems) {
        await axios.delete(`${API}/data/permanent-delete/${item._id}?session_id=${sessionId}`);
      }
      fetchTrash();
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("Empty trash error:", err);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash className="h-5 w-5" />
            Papierkorb
            {trashItems.length > 0 && (
              <span className="text-sm font-normal text-muted-foreground">
                ({trashItems.length} {trashItems.length === 1 ? "Eintrag" : "Einträge"})
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full" />
              <span className="ml-2 text-muted-foreground text-sm">Lade...</span>
            </div>
          ) : trashItems.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              Der Papierkorb ist leer.
            </div>
          ) : (
            <div className="space-y-2">
              {trashItems.map((item) => {
                const name = `${item.vorname || ""} ${item.nachname || ""}`.trim();
                const deletedAt = item.deleted_at ? new Date(item.deleted_at).toLocaleString("de-DE") : "";
                return (
                  <div
                    key={item._id}
                    data-testid={`trash-item-${item._id}`}
                    className="flex items-center justify-between gap-3 p-3 bg-secondary/50 border border-border rounded"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{name || "Unbekannt"}</p>
                      <p className="text-xs text-muted-foreground">
                        {item.spieler_lizenz && <span className="mr-2">{item.spieler_lizenz}</span>}
                        {item.schiedsrichter_lizenz && <span className="mr-2">{item.schiedsrichter_lizenz}</span>}
                        {item.mannschaft && <span className="mr-2">{item.mannschaft}</span>}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        Gelöscht {deletedAt} von {item.deleted_by || "unbekannt"}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        data-testid={`restore-trash-${item._id}`}
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => handleRestore(item._id, name)}
                      >
                        <ArrowCounterClockwise className="h-3 w-3 mr-1" />
                        Wiederherstellen
                      </Button>
                      <Button
                        data-testid={`permanent-delete-${item._id}`}
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => handlePermanentDelete(item._id, name)}
                      >
                        <Trash className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {trashItems.length > 0 && (
          <div className="flex justify-between items-center pt-3 border-t border-border">
            <Button
              data-testid="empty-trash-button"
              variant="destructive"
              size="sm"
              className="text-xs"
              onClick={handleEmptyTrash}
            >
              <Trash className="h-3 w-3 mr-1" />
              Papierkorb leeren
            </Button>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => onOpenChange(false)}>
              Schließen
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
